// Today's factor environment and the month-by-month resemblance strip, from
// POST /api/performance/explore/environment (`factor-environment.v2`).
import { isIsoDate } from './exploreFormat';

export const ENVIRONMENT_FEATURE_VERSION = 'factor-environment.v2';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_KEY = /^(\d{4})-(0[1-9]|1[0-2])$/;

const isMonthKey = (value) => typeof value === 'string' && MONTH_KEY.test(value);

// "Mar 2020" for "2020-03".
export function monthLabel(month) {
    const match = MONTH_KEY.exec(month || '');
    return match ? `${MONTHS[Number(match[2]) - 1]} ${match[1]}` : '—';
}

export const monthOf = (iso) => (isIsoDate(iso) ? iso.slice(0, 7) : null);

const lastDayOfMonth = (month) => {
    const [year, number] = month.split('-').map(Number);
    return `${month}-${String(new Date(Date.UTC(year, number, 0)).getUTCDate()).padStart(2, '0')}`;
};

// The first day of the month before `iso`'s: where a period's lead-in starts.
export function leadInStart(iso) {
    if (!isIsoDate(iso)) return null;
    const [year, month] = iso.split('-').map(Number);
    return month === 1 ? `${year - 1}-12-01` : `${year}-${String(month - 1).padStart(2, '0')}-01`;
}

/**
 * Months picked on the strip as a period: the first day of the first month to
 * the last day of the last, clipped to the factor history. Null when the
 * clipped span is empty.
 */
export function monthSpanPeriod(fromMonth, toMonth, firstDate = null, lastDate = null) {
    if (!isMonthKey(fromMonth) || !isMonthKey(toMonth)) return null;
    const [startMonth, endMonth] = fromMonth <= toMonth ? [fromMonth, toMonth] : [toMonth, fromMonth];
    let start = `${startMonth}-01`;
    let end = lastDayOfMonth(endMonth);
    if (isIsoDate(firstDate) && start < firstDate) start = firstDate;
    if (isIsoDate(lastDate) && end > lastDate) end = lastDate;
    if (start > end) return null;
    return { start_date: start, end_date: end };
}

// Each event as a band over the months it spans, inside the shown months.
export function eventBands(columns, months) {
    if (!months.length) return [];
    const first = months[0];
    const last = months[months.length - 1];
    return (columns || []).flatMap((column) => {
        const from = monthOf(column?.start_date);
        const to = monthOf(column?.end_date);
        if (!from || !to || to < first || from > last) return [];
        return [{ id: column.id, tag: column.tag, name: column.name, x1: from < first ? first : from, x2: to > last ? last : to }];
    });
}

// The events whose months include `month`.
export const eventsIn = (columns, month) => (columns || [])
    .filter((column) => monthOf(column?.start_date) <= month && month <= monthOf(column?.end_date));

const spanOf = (column) => Date.parse(column.end_date) - Date.parse(column.start_date);

/**
 * The event a click on `month` selects: of the events over it, the shortest
 * first. A click on the selected one moves to the next, so events that
 * overlap can each be reached.
 */
export function eventAt(columns, month, selectedId = null) {
    const over = eventsIn(columns, month).sort((left, right) => spanOf(left) - spanOf(right)
        || left.start_date.localeCompare(right.start_date) || left.id.localeCompare(right.id));
    if (!over.length) return null;
    return over[(over.findIndex((column) => column.id === selectedId) + 1) % over.length];
}

// A month draws dashed for a portfolio when more of its factor exposure than
// this had no factor history.
export const HIGH_UNCOVERED_SHARE = 0.1;

// Whether row `index` of a timeline point is such a month.
export const thinMonth = (point, index) => Number.isFinite(point?.[`drawdown${index}`])
    && (point[`uncovered${index}`] ?? 0) > HIGH_UNCOVERED_SHARE;

const finite = (value) => (Number.isFinite(value) ? value : null);
const LEVELS = Object.freeze({ close: 'close', high: 'high', low: 'low', within: 'within_month_drawdown' });
const rowKey = (index) => (name) => `${name}${index}`;
const marketKey = (index) => (name) => `market${name[0].toUpperCase()}${name.slice(1)}${index}`;

/**
 * One point per month for both panels, in order: the market's score (0–100,
 * or null) and whether the month is too recent to compare, then for row `i`
 * the month's close, high and low of its cumulative replay (`close${i}`,
 * `high${i}`, `low${i}`), its worst drawdown within the month (`within${i}`)
 * and its uncovered share (`uncovered${i}`), and for market `j` the same of
 * its actual total-return level (`marketClose${j}`, `marketHigh${j}`,
 * `marketLow${j}`, `marketWithin${j}`). `lines` are grid rows'
 * `monthly_replay`; `marketLines` are market rows' `monthly_drawdown`. Months
 * join by key; `windowPoints` draws the lines over the months shown.
 */
export function timelinePoints(months, lines, marketLines = []) {
    const byMonth = new Map();
    const at = (month) => {
        if (!byMonth.has(month)) byMonth.set(month, { month, score: null, excluded: false });
        return byMonth.get(month);
    };
    (months || []).forEach((row) => {
        if (!isMonthKey(row?.month)) return;
        Object.assign(at(row.month), { score: finite(row.score), excluded: row.excluded === true });
    });
    const take = (line, key, extra = {}) => (line?.months || []).forEach((month, position) => {
        if (!isMonthKey(month)) return;
        const point = at(month);
        Object.entries(LEVELS).forEach(([name, field]) => { point[key(name)] = finite(line[field]?.[position]); });
        Object.entries(extra).forEach(([name, field]) => { point[name] = finite(line[field]?.[position]); });
    });
    lines.forEach((line, index) => take(line, rowKey(index), { [`uncovered${index}`]: 'uncovered_exposure_share' }));
    marketLines.forEach((line, index) => take(line, marketKey(index)));
    return [...byMonth.values()].sort((left, right) => left.month.localeCompare(right.month));
}

/**
 * Points `lo` to `hi` of `timelinePoints` as drawn, re-zeroed as the rest of
 * the Performance tab re-zeroes its window: each line's first peak is its
 * level at the close before the first month shown, or where it opens when it
 * starts inside them. Row `i`'s worst drawdown each month below its peak so
 * far (`drawdown${i}`) adds up as the replay does, min(low − peak, within);
 * market `j`'s (`market${j}`) compounds, min(low ÷ peak − 1, within). The
 * peak then rises to the month's high. Each row's drawdown also splits into
 * `solid${i}` and `dashed${i}`: a dashed run takes the months either side of
 * it, so the line stays joined.
 */
export function windowPoints(points, lo, hi, count, marketCount = 0) {
    const shown = points.slice(lo, hi + 1).map((point) => ({ ...point }));
    const draw = (key, out, compound) => {
        let peak = compound ? 1 : 0;
        for (let at = lo - 1; at >= 0; at -= 1) {
            if (Number.isFinite(points[at][key('close')])) {
                peak = points[at][key('close')];
                break;
            }
        }
        shown.forEach((point) => {
            const [low, high, within] = [point[key('low')], point[key('high')], point[key('within')]];
            if (![low, high, within].every(Number.isFinite)) {
                point[out] = null;
                return;
            }
            point[out] = compound ? Math.min(low / peak - 1, within) : Math.min(low - peak, within);
            peak = Math.max(peak, high);
        });
    };
    for (let index = 0; index < count; index += 1) draw(rowKey(index), `drawdown${index}`, false);
    for (let index = 0; index < marketCount; index += 1) draw(marketKey(index), `market${index}`, true);
    for (let index = 0; index < count; index += 1) {
        shown.forEach((point, position) => {
            const near = [shown[position - 1], point, shown[position + 1]].some((next) => thinMonth(next, index));
            point[`solid${index}`] = thinMonth(point, index) ? null : point[`drawdown${index}`];
            point[`dashed${index}`] = near ? point[`drawdown${index}`] : null;
        });
    }
    return shown;
}

// At least 1%, since the ticks read in whole percents.
const DRAWDOWN_STEPS = Object.freeze([0.01, 0.02, 0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5]);

// The replay panel's y axis for `count` rows and `marketCount` markets: 0
// down to a round step below the deepest drawdown shown, with a tick on each
// step (at most four).
export function drawdownScale(points, count, marketCount = 0) {
    const keys = [...Array.from({ length: count }, (_, index) => `drawdown${index}`),
        ...Array.from({ length: marketCount }, (_, index) => `market${index}`)];
    let lowest = 0;
    points.forEach((point) => keys.forEach((key) => {
        const value = point[key];
        if (Number.isFinite(value) && value < lowest) lowest = value;
    }));
    const step = DRAWDOWN_STEPS.find((size) => -lowest / size <= 4) ?? DRAWDOWN_STEPS[DRAWDOWN_STEPS.length - 1];
    const steps = Math.max(1, Math.ceil(-lowest / step - 1e-9));
    return { domain: [-steps * step, 0], ticks: Array.from({ length: steps + 1 }, (_, index) => (index - steps) * step) };
}

/**
 * The market panel's caveat. With the validation study's numbers (the
 * route's `methodology.study`), it says that past close matches were followed
 * by stress no more often than any month-end, which is why it is context.
 */
export function contextNote(methodology) {
    const study = methodology?.study;
    const numbers = [study?.signal_hit_rate, study?.base_rate, study?.horizon_trading_days];
    if (!numbers.every(Number.isFinite)) return 'Context, not a forecast.';
    const [hit, base, days] = numbers;
    return `Context, not a forecast: past month-ends that closely matched a stress run-up were followed by stress within ${days} trading days `
        + `no more often than any month-end (${Math.round(hit * 100)}% against ${Math.round(base * 100)}%).`;
}

// The greyed months at the end that never take part in matching.
export function recentSpan(points) {
    const first = points.findIndex((point) => point.excluded);
    return first < 0 ? null : { x1: points[first].month, x2: points[points.length - 1].month };
}

// Axis ticks for the shown months: January every five, two or one years
// apart, or every quarter when two years or less are shown.
export function timelineTicks(points) {
    const count = points.length;
    let step = 3;
    if (count > 180) step = 60;
    else if (count > 72) step = 24;
    else if (count > 24) step = 12;
    return points.filter((point) => {
        const [year, month] = point.month.split('-').map(Number);
        return (year * 12 + month - 1) % step === 0;
    }).map((point) => point.month);
}

// The month most like today, for the chart's summary.
export function closestMonth(points) {
    return points.reduce((best, point) => (point.score !== null && (!best || point.score > best.score) ? point : best), null);
}

// The closest past pattern when today matches one: the run-up of its
// episode, the month before the episode starts and the month it starts, and
// the episode's name.
export function matchedRunUp(resemblance) {
    if (resemblance?.status !== 'match') return null;
    const from = monthOf(leadInStart(resemblance.episode?.window_start));
    const to = monthOf(resemblance.episode?.window_start);
    const name = resemblance.episode?.name;
    return from && to && name ? { from, to, name } : null;
}
